import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TranslocoPipe } from '@jsverse/transloco';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { FieldLaborAssignment, FieldLaborRequirement, FieldWorkPackage, FieldWorkTargetType, QuickDeliveryHandlingResult } from './field-work.models';
import { FieldWorkExecutionDialogComponent } from './field-work-execution-dialog.component';
import { FieldWorkLocationOption, FieldWorkPackageCreateDialogComponent } from './field-work-package-create-dialog.component';
import { FieldWorkLaborPlanningDialogComponent } from './field-work-labor-planning-dialog.component';
import { QuickDeliveryHandlingDialogComponent } from './quick-delivery-handling-dialog.component';
import { QuickDeliveryHandlingPolicyDialogComponent } from './quick-delivery-handling-policy-dialog.component';
import { FieldWorkService } from './field-work.service';

@Component({
  selector: 'app-field-resources-panel',
  standalone: true,
  imports: [
    CommonModule, RouterLink, ButtonModule, TagModule, TranslocoPipe,
    FieldWorkPackageCreateDialogComponent, FieldWorkLaborPlanningDialogComponent,
    FieldWorkExecutionDialogComponent, QuickDeliveryHandlingDialogComponent,
    QuickDeliveryHandlingPolicyDialogComponent
  ],
  template: `
    <section class="field-panel" *ngIf="canView">
      <header>
        <div>
          <span class="eyebrow">{{ 'fieldWork.fieldResources' | transloco }}</span>
          <h3>{{ 'fieldWork.resourcesSubtitle' | transloco }}</h3>
          <p>{{ 'fieldWork.subtitle' | transloco }}</p>
        </div>
        <div class="header-actions">
          <button *ngIf="canQuickHandle" pButton type="button" label="Record handling"
            icon="pi pi-bolt" severity="success" (click)="openQuickHandling()"></button>
          <button *ngIf="canQuickHandlingPolicy" pButton type="button" label="Handling policy"
            icon="pi pi-sliders-h" severity="secondary" [outlined]="true"
            (click)="quickHandlingPolicyVisible.set(true)"></button>
          <button *ngIf="canManage" pButton type="button" [label]="'fieldWork.addPackage' | transloco"
            icon="pi pi-plus" (click)="openCreateDialog()"></button>
          <button *ngIf="targetType === 'POSM_DELIVERY_SCHEDULE' && canManage" pButton type="button"
            [label]="'fieldWork.importLegacy' | transloco" icon="pi pi-download" severity="secondary" outlined
            [loading]="importing" (click)="importLegacy()"></button>
          <a pButton routerLink="/operations/field-work/work-packages" [label]="'fieldWork.openWorkbench' | transloco"
            icon="pi pi-external-link" severity="secondary" outlined></a>
        </div>
      </header>

      <div class="message error" *ngIf="error">{{ error }}</div>
      <div class="metrics">
        <span><strong>{{ packages.length }}</strong> {{ 'fieldWork.workPackages' | transloco }}</span>
        <span><strong>{{ laborAssigned }}</strong> {{ 'fieldWork.peopleAssigned' | transloco }}</span>
        <span *ngIf="canViewEquipment"><strong>{{ equipmentReserved }}</strong> {{ 'fieldWork.equipmentReserved' | transloco }}</span>
        <span *ngIf="canViewRates"><strong>{{ estimatedCost | number:'1.2-2' }}</strong> {{ 'fieldWork.estimatedCost' | transloco }}</span>
        <span *ngIf="canViewRates"><strong>{{ estimatedRevenue | number:'1.2-2' }}</strong> {{ 'fieldWork.estimatedRevenue' | transloco }}</span>
      </div>

      <div class="packages" *ngIf="packages.length; else empty">
        <article class="package" *ngFor="let work of packages">
          <div class="package-heading">
            <div>
              <strong>{{ work.title }}</strong>
              <small>{{ work.workPackageNumber }} · {{ work.activityType }}</small>
            </div>
            <div class="package-heading-actions">
              <button *ngIf="canManage && ['DRAFT', 'PLANNED'].includes(work.status || 'DRAFT')"
                pButton type="button" label="Edit package" icon="pi pi-pencil" size="small"
                severity="secondary" outlined (click)="openEditDialog(work)"></button>
              <button *ngIf="['DRAFT', 'PLANNED'].includes(work.status || 'DRAFT') && (canManage || canAssign || canRelease)"
                pButton type="button" [label]="work.assignmentAllowed ? 'Plan & assign resources' : 'Gather requirements'"
                icon="pi pi-briefcase" size="small" outlined
                (click)="openLaborPlanning(work)"></button>
              <p-tag [value]="work.status || 'DRAFT'" [severity]="severity(work.status)"></p-tag>
            </div>
          </div>
          <div class="schedule">
            <span><i class="pi pi-calendar"></i> {{ work.plannedStart | date:'short' }} – {{ work.plannedEnd | date:'short' }}</span>
            <span><i class="pi pi-map-marker"></i> {{ work.locationName || 'Target location' }}</span>
          </div>
          <div class="blockers" *ngIf="work.blockers?.length">
            <span *ngFor="let blocker of work.blockers">{{ blocker }}</span>
          </div>
          <div class="stage-note" *ngIf="work.requirementOnly">
            <i class="pi pi-info-circle"></i>
            Requirements only at this stage. People and equipment are assigned after the order release enters truck or bulk planning, or on shipment delivery.
          </div>

          <div class="resource-grid">
            <section>
              <h4>{{ 'fieldWork.laborRoles' | transloco }}</h4>
              <div class="resource-row" *ngFor="let requirement of work.laborRequirements">
                <div>
                  <strong>{{ requirement.roleName }}</strong>
                  <small>{{ assignedFor(work, requirement) }}/{{ requirement.headcount }} assigned</small>
                  <small *ngIf="requirement.skills?.length">
                    {{ skillLabels(requirement) }}
                  </small>
                </div>
                <button *ngIf="work.assignmentAllowed && (canAssign || canManage)" pButton type="button"
                  label="Manage slots" icon="pi pi-users" size="small" text (click)="openLaborPlanning(work)"></button>
                <div class="labor-slot-grid" *ngIf="work.assignmentAllowed">
                  <div class="labor-slot" *ngFor="let slot of laborSlots(work, requirement)">
                    <span>Slot {{ slot.slotNumber }}</span>
                    <strong>{{ slot.assignment?.workerName || 'Unassigned' }}</strong>
                    <small *ngIf="slot.assignment">{{ slot.assignment.sourceType }} · {{ slot.assignment.assignmentStatus }}</small>
                    <div class="row-actions" *ngIf="canAssign || canManage">
                      <button pButton type="button" size="small" text
                        [label]="slot.assignment ? 'Change' : 'Assign'" icon="pi pi-user-edit"
                        (click)="openLaborPlanning(work)"></button>
                      <button *ngIf="slot.assignment && canAssign" pButton type="button" size="small" text severity="danger"
                        label="Clear" icon="pi pi-times" (click)="clearLaborSlot(work, requirement, slot.slotNumber)"></button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="actual-row" *ngFor="let assignment of work.laborAssignments">
                <div>
                  <strong>{{ assignment.workerName }}</strong>
                  <small>{{ assignment.sourceType }} · {{ assignment.assignmentStatus }}</small>
                </div>
              </div>
              <button *ngIf="['DRAFT', 'PLANNED'].includes(work.status || 'DRAFT') && (canManage || canAssign)"
                pButton type="button" [label]="work.assignmentAllowed ? 'Manage resource plan' : 'Manage labor requirements'"
                icon="pi pi-users" size="small" text
                (click)="openLaborPlanning(work)"></button>
            </section>

            <section *ngIf="canViewEquipment">
              <h4>{{ 'fieldWork.toolsEquipment' | transloco }}</h4>
              <div class="resource-row" *ngFor="let requirement of work.equipmentRequirements">
                <div>
                  <strong>{{ requirement.categoryName }}</strong>
                  <small>{{ requirement.allocatedQuantity || 0 }}/{{ requirement.requiredQuantity }} reserved</small>
                </div>
              </div>
              <div class="allocation" *ngFor="let allocation of work.equipmentAllocations">
                <div>
                  <strong>{{ allocation.equipmentResourceName }}</strong>
                  <small>{{ allocation.allocationStatus }} · {{ allocation.ownershipType }}</small>
                </div>
              </div>
              <button *ngIf="['DRAFT', 'PLANNED'].includes(work.status || 'DRAFT') && (canManageEquipment || canAssign)"
                pButton type="button" [label]="work.assignmentAllowed ? 'Manage equipment plan' : 'Manage equipment requirements'"
                icon="pi pi-wrench" size="small" text
                (click)="openLaborPlanning(work)"></button>
            </section>
          </div>

          <div class="package-actions" *ngIf="['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(work.status || '')
            && (canExecute || canApprove || canCheckout || canRelease)">
            <button pButton type="button" [label]="work.status === 'COMPLETED' ? 'View actuals' : 'Execute work'"
              icon="pi pi-play-circle" (click)="openExecution(work)"></button>
          </div>
        </article>
      </div>
      <ng-template #empty><div class="empty">{{ 'fieldWork.noPackages' | transloco }}</div></ng-template>

      <app-field-work-package-create-dialog
        [visible]="createDialogVisible()"
        (visibleChange)="createDialogVisible.set($event)"
        [value]="packageDraft"
        [saving]="saving()"
        [error]="createError()"
        [locationOptions]="locationOptions"
        (submitted)="createPackage($event)">
      </app-field-work-package-create-dialog>

      <app-field-work-labor-planning-dialog
        [visible]="laborPlanningVisible()"
        (visibleChange)="laborPlanningVisible.set($event)"
        [work]="planningWork()"
        [canManage]="canManage"
        [canAssign]="canAssign"
        [canRelease]="canRelease"
        [canViewEquipment]="canViewEquipment"
        [canManageEquipment]="canManageEquipment"
        [canOverride]="canOverride"
        [canViewLaborRates]="canViewLaborRates"
        [canViewEquipmentRates]="canViewEquipmentRates"
        (changed)="onLaborPlanningChanged($event)">
      </app-field-work-labor-planning-dialog>

      <app-field-work-execution-dialog
        [visible]="executionVisible()"
        (visibleChange)="executionVisible.set($event)"
        [work]="executionWork()"
        [canExecute]="canExecute"
        [canApprove]="canApprove"
        [canCheckout]="canCheckout"
        [canRelease]="canRelease"
        [canViewEquipment]="canViewEquipment"
        [canViewRates]="canViewRates"
        (changed)="onExecutionChanged($event)">
      </app-field-work-execution-dialog>

      <app-quick-delivery-handling-dialog
        [visible]="quickHandlingVisible()"
        (visibleChange)="quickHandlingVisible.set($event)"
        [deliveryId]="targetId"
        [deliveryReference]="targetReference"
        [defaultDriverId]="defaultDriverId"
        (completed)="onQuickHandlingCompleted($event)"
        (controlledFieldWorkRequested)="useControlledFieldWork()">
      </app-quick-delivery-handling-dialog>

      <app-quick-delivery-handling-policy-dialog
        [visible]="quickHandlingPolicyVisible()"
        (visibleChange)="quickHandlingPolicyVisible.set($event)">
      </app-quick-delivery-handling-policy-dialog>

    </section>
  `,
  styles: [`
    .field-panel{border:1px solid var(--p-content-border-color);border-radius:1rem;background:var(--p-content-background);padding:1rem;margin:1rem 0}
    header,.package-heading,.resource-row,.allocation,.candidate{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem}
    header{margin-bottom:.75rem}.header-actions,.package-actions,.row-actions,.package-heading-actions{display:flex;gap:.45rem;flex-wrap:wrap}
    h3,h4{margin:.15rem 0}p,small{color:var(--p-text-muted-color)}small{display:block;margin-top:.15rem}
    .eyebrow{color:var(--p-primary-color);font-size:.72rem;font-weight:750;text-transform:uppercase;letter-spacing:.08em}
    .metrics{display:flex;gap:.55rem;flex-wrap:wrap;margin:.8rem 0}.metrics span{padding:.5rem .7rem;border-radius:.65rem;background:color-mix(in srgb,var(--p-primary-color) 8%,transparent)}
    .packages{display:grid;gap:.8rem}.package{border:1px solid var(--p-content-border-color);border-radius:.8rem;padding:.85rem}
    .schedule{display:flex;gap:1rem;flex-wrap:wrap;color:var(--p-text-muted-color);font-size:.8rem;margin:.55rem 0}
    .resource-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}.resource-grid section{background:var(--p-content-hover-background);border-radius:.65rem;padding:.7rem}
    .resource-row,.allocation,.actual-row{padding:.45rem 0;border-bottom:1px solid var(--p-content-border-color)}
    .resource-row{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:start}.labor-slot-grid{grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:.45rem;margin-top:.5rem}
    .labor-slot{display:grid;gap:.12rem;padding:.5rem .6rem;border:1px solid var(--p-content-border-color);border-radius:.55rem;background:var(--p-content-background)}.labor-slot>span{font-size:.72rem;color:var(--p-text-muted-color);text-transform:uppercase;font-weight:700}.labor-slot .row-actions{margin-top:.2rem}
    .empty{padding:1.5rem;text-align:center;color:var(--p-text-muted-color)}.message{padding:.6rem;border-radius:.5rem;margin:.5rem 0}.error{background:var(--p-red-50);color:var(--p-red-700)}
    .blockers{display:grid;gap:.25rem;margin:.5rem 0}.blockers span{color:var(--p-red-600);font-size:.75rem}
    .stage-note{display:flex;gap:.45rem;align-items:flex-start;margin:.55rem 0;padding:.6rem .7rem;border-radius:.55rem;background:var(--p-blue-50);color:var(--p-blue-700);font-size:.8rem}
    @media(max-width:760px){header,.resource-grid{display:flex;flex-direction:column}.resource-grid{gap:.6rem}}
  `]
})
export class FieldResourcesPanelComponent implements OnChanges {
  private readonly service = inject(FieldWorkService);
  private readonly permissions = inject(PermissionService);

  @Input({ required: true }) targetType!: FieldWorkTargetType;
  @Input({ required: true }) targetId?: number;
  @Input() targetReference?: string;
  @Input() logisticsProjectId?: number;
  @Input() customerCrmClientId?: number;
  @Input() locationId?: number;
  @Input() locationName?: string;
  @Input() locationOptions?: FieldWorkLocationOption[];
  /** Fleet identity of the delivery's selected driver, when one is assigned. */
  @Input() defaultDriverId?: number;

  readonly canView = this.permissions.hasPermission(app_permissions.operations.fieldWork.view);
  readonly canManage = this.permissions.hasPermission(app_permissions.operations.fieldWork.manage);
  readonly canAssign = this.permissions.hasPermission(app_permissions.operations.fieldWork.assign);
  readonly canRelease = this.permissions.hasPermission(app_permissions.operations.fieldWork.release);
  readonly canExecute = this.permissions.hasPermission(app_permissions.operations.fieldWork.execute);
  readonly canApprove = this.permissions.hasPermission(app_permissions.operations.fieldWork.approve);
  readonly canCheckout = this.permissions.hasPermission(app_permissions.operations.equipment.checkout);
  readonly canViewEquipment = this.permissions.hasPermission(app_permissions.operations.equipment.view);
  readonly canManageEquipment = this.permissions.hasPermission(app_permissions.operations.equipment.manage);
  readonly canOverride = this.permissions.hasPermission(app_permissions.operations.fieldWork.override);
  readonly canViewLaborRates = this.permissions.hasAnyPermission([
    app_permissions.accounts.workforce_finance.view,
    app_permissions.accounts.workforce_finance.rates_manage,
    app_permissions.operations.fieldWork.financeView
  ]);
  readonly canViewEquipmentRates = this.permissions.hasAnyPermission([
    app_permissions.operations.equipment.ratesView,
    app_permissions.operations.equipment.ratesManage,
    app_permissions.operations.fieldWork.financeView
  ]);
  readonly canViewRates = this.permissions.hasAnyPermission([
    app_permissions.accounts.workforce_finance.view,
    app_permissions.operations.equipment.ratesView,
    app_permissions.operations.fieldWork.financeView
  ]);
  packages: FieldWorkPackage[] = [];
  packageDraft: FieldWorkPackage = {};
  readonly createDialogVisible = signal(false);
  readonly createError = signal('');
  readonly laborPlanningVisible = signal(false);
  readonly planningWork = signal<FieldWorkPackage | undefined>(undefined);
  readonly executionVisible = signal(false);
  readonly executionWork = signal<FieldWorkPackage | undefined>(undefined);
  readonly quickHandlingVisible = signal(false);
  readonly quickHandlingPolicyVisible = signal(false);
  readonly saving = signal(false);
  importing = false;
  error = '';

  ngOnChanges(): void {
    this.resetPackageDraft();
    this.load();
  }

  get laborAssigned(): number {
    return this.packages.reduce((sum, work) => sum + (work.laborAssignments?.length || 0), 0);
  }

  get canQuickHandle(): boolean {
    return this.targetType === 'SHIPMENT_DELIVERY' && Boolean(this.targetId) && this.canExecute;
  }

  get canQuickHandlingPolicy(): boolean {
    return this.targetType === 'SHIPMENT_DELIVERY' && this.canManage;
  }
  get equipmentReserved(): number {
    return this.packages.reduce((sum, work) => sum + (work.equipmentAllocations || [])
      .filter(row => !['CANCELLED', 'RETURNED'].includes(row.allocationStatus || '')).length, 0);
  }
  get estimatedCost(): number {
    return this.packages.flatMap(work => work.equipmentAllocations || [])
      .reduce((sum, row) => sum + (row.plannedQuantity || 0) * (row.buyRateSnapshot || 0), 0)
      + this.packages.flatMap(work => work.laborAssignments || [])
        .reduce((sum, row) => sum + (row.plannedQuantity || 0) * (row.buyRateSnapshot || 0), 0);
  }
  get estimatedRevenue(): number {
    return this.packages.flatMap(work => work.equipmentAllocations || [])
      .reduce((sum, row) => sum + (row.plannedQuantity || 0) * (row.sellRateSnapshot || 0), 0)
      + this.packages.flatMap(work => work.laborAssignments || [])
        .reduce((sum, row) => sum + (row.plannedQuantity || 0) * (row.sellRateSnapshot || 0), 0);
  }

  openCreateDialog(): void {
    this.createError.set('');
    this.resetPackageDraft();
    this.createDialogVisible.set(true);
  }

  openEditDialog(work: FieldWorkPackage): void {
    if (!this.canManage || !work.fieldWorkPackageId
      || !['DRAFT', 'PLANNED'].includes(work.status || 'DRAFT')) return;
    this.createError.set('');
    this.packageDraft = { ...work };
    this.createDialogVisible.set(true);
  }

  createPackage(value: FieldWorkPackage): void {
    if (!this.targetId) return;
    this.saving.set(true);
    this.createError.set('');
    this.service.savePackage(value).pipe(
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: () => {
        this.createDialogVisible.set(false);
        this.resetPackageDraft();
        this.load();
      },
      error: error => {
        this.createError.set(this.errorMessage(error));
      }
    });
  }

  openLaborPlanning(work: FieldWorkPackage): void {
    this.planningWork.set(work);
    this.laborPlanningVisible.set(true);
  }

  onLaborPlanningChanged(work: FieldWorkPackage): void {
    this.planningWork.set(work);
    this.packages = this.packages.map(current =>
      current.fieldWorkPackageId === work.fieldWorkPackageId ? work : current);
  }

  openExecution(work: FieldWorkPackage): void {
    this.executionWork.set(work);
    this.executionVisible.set(true);
  }

  openQuickHandling(): void {
    if (!this.canQuickHandle) return;
    this.error = '';
    this.quickHandlingVisible.set(true);
  }

  onQuickHandlingCompleted(result: QuickDeliveryHandlingResult): void {
    this.load();
    if (result.controlledFieldWorkRequired) {
      this.error = result.exceptions?.join(' ') || result.message || 'This handling job needs Controlled Field Work.';
    }
  }

  useControlledFieldWork(): void {
    this.quickHandlingVisible.set(false);
    this.openCreateDialog();
  }

  onExecutionChanged(work: FieldWorkPackage): void {
    this.executionWork.set(work);
    this.packages = this.packages.map(current =>
      current.fieldWorkPackageId === work.fieldWorkPackageId ? work : current);
  }

  importLegacy(): void {
    if (!this.targetId) return;
    this.importing = true;
    this.service.importLegacyPosm(this.targetId).subscribe({
      next: () => { this.importing = false; this.load(); },
      error: error => { this.importing = false; this.capture(error); }
    });
  }

  assignedFor(work: FieldWorkPackage, requirement: FieldLaborRequirement): number {
    return (work.laborAssignments || []).filter(row =>
      row.fieldLaborRequirementId === requirement.fieldLaborRequirementId
      && !['CANCELLED', 'REJECTED'].includes(row.assignmentStatus || '')).length;
  }

  laborSlots(work: FieldWorkPackage, requirement: FieldLaborRequirement): Array<{
    slotNumber: number; assignment?: FieldLaborAssignment;
  }> {
    const assignments = (work.laborAssignments || []).filter(row =>
      row.fieldLaborRequirementId === requirement.fieldLaborRequirementId
      && !['CANCELLED', 'REJECTED'].includes(row.assignmentStatus || ''));
    return Array.from({ length: Math.max(0, requirement.headcount || 0) }, (_, index) => ({
      slotNumber: index + 1,
      assignment: assignments.find(row => row.slotNumber === index + 1)
    }));
  }

  clearLaborSlot(work: FieldWorkPackage, requirement: FieldLaborRequirement, slotNumber: number): void {
    if (!this.canAssign || !work.fieldWorkPackageId || !requirement.fieldLaborRequirementId) return;
    this.error = '';
    this.service.clearLaborSlot(work.fieldWorkPackageId, requirement.fieldLaborRequirementId, slotNumber).subscribe({
      next: () => this.load(),
      error: error => this.capture(error)
    });
  }

  skillLabels(requirement: FieldLaborRequirement): string {
    return (requirement.skills || []).map(skill =>
      `${skill.skillName}${skill.minimumProficiencyLevel ? ` (${skill.minimumProficiencyLevel})` : ''}`).join(', ');
  }

  severity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(status || '')) return 'success';
    if (['DRAFT', 'PLANNED'].includes(status || '')) return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'info';
  }

  private load(): void {
    if (!this.canView || !this.targetId) return;
    this.service.packages({ targetType: this.targetType, targetId: this.targetId }).subscribe({
      next: packages => {
        this.packages = packages;
        const planningId = this.planningWork()?.fieldWorkPackageId;
        if (planningId) {
          const refreshedPlanningWork = packages.find(work => work.fieldWorkPackageId === planningId);
          if (refreshedPlanningWork) this.planningWork.set(refreshedPlanningWork);
        }
        const executionId = this.executionWork()?.fieldWorkPackageId;
        if (executionId) {
          const refreshedExecutionWork = packages.find(work => work.fieldWorkPackageId === executionId);
          if (refreshedExecutionWork) this.executionWork.set(refreshedExecutionWork);
        }
      },
      error: error => this.capture(error)
    });
  }

  private resetPackageDraft(): void {
    const start = new Date();
    const end = new Date(start.getTime() + 8 * 60 * 60 * 1000);
    this.packageDraft = {
      title: `${this.targetReference || this.targetType} field work`,
      activityType: this.targetType === 'POSM_DELIVERY_SCHEDULE' ? 'INSTALLATION' : 'GENERAL',
      targetType: this.targetType,
      targetId: this.targetId,
      targetReference: this.targetReference,
      logisticsProjectId: this.logisticsProjectId,
      customerCrmClientId: this.customerCrmClientId,
      locationId: this.locationId,
      locationName: this.locationName,
      plannedStart: this.localDateTime(start),
      plannedEnd: this.localDateTime(end),
      billable: false,
      status: 'DRAFT'
    };
  }

  private localDateTime(value: Date): string {
    const offset = value.getTimezoneOffset() * 60000;
    return new Date(value.getTime() - offset).toISOString().slice(0, 16);
  }

  private capture(error: any): void {
    this.error = this.errorMessage(error);
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message || 'Field work action failed.';
  }
}
